// Product category visibility per channel (เว็บ / สมาชิก / แอป) — pure helpers.
//
// Spec: mdf-project docs/specs/2026-10-05-category-channel-visibility-design.md
//   §4 API contract, §6 UI plan, §1 E1-E3.
//
// WHY THIS FILE EXISTS. This repo's jest cannot mount a .vue (memory
// reference-bo-fn-jest-infra), so the rules the Categories page depends on —
// which flags exist, what a single-switch PATCH body is, when a row counts as
// hidden everywhere, how a missing/legacy value reads — live here as plain
// functions that tests can call directly. No Vue, no store, no axios.

// The three channel flags, in table/dialog order (เว็บ · สมาชิก · แอป). The BE
// columns and JSON keys are exactly these names (spec §2, §4.1).
export const VISIBILITY_FIELDS = Object.freeze(["show_web", "show_member", "show_app"]);

// field → i18n key for its column header / checkbox label (spec §6 i18n table).
export const VISIBILITY_LABEL_KEYS = Object.freeze({
    show_web: "app_catalog.col_show_web",
    show_member: "app_catalog.col_show_member",
    show_app: "app_catalog.col_show_app"
});

export function isVisibilityField(field) {
    return VISIBILITY_FIELDS.indexOf(field) !== -1;
}

// visibilityPatch → the PATCH /categories/:id/visibility body for ONE switch.
// Only the changed flag is sent (spec §6 "ส่งเฉพาะ field ที่เปลี่ยน"): sending all
// three would overwrite a flag another admin changed meanwhile with this tab's
// stale copy. An unknown field is a programming error, so it throws instead of
// sending a body the BE would answer with 400 "no visibility field supplied".
export function visibilityPatch(field, value) {
    if (!isVisibilityField(field)) {
        throw new Error("unknown visibility field: " + field);
    }
    return { [field]: value === true };
}

// flagOf reads one flag the way the BE defines it: the DDL default is true and
// an older API (or a row from before the SQL) may not send the key at all, so
// only an explicit `false` means hidden.
export function flagOf(row, field) {
    return !(row && row[field] === false);
}

// readVisibility → {show_web, show_member, show_app} as strict booleans, for
// seeding the edit dialog (undefined ⇒ true, spec §6 openEdit).
export function readVisibility(row) {
    const out = {};
    VISIBILITY_FIELDS.forEach(f => {
        out[f] = flagOf(row, f);
    });
    return out;
}

// defaultVisibility → what a brand-new category starts with (all ticked).
export function defaultVisibility() {
    return readVisibility(null);
}

// ---- single switch (owner follow-up 2026-10-05: "เปิด ปิด แค่ปุ่มเดียวพอ มีผลทั้ง
// 3 ที่"). The BO shows ONE switch; the BE still stores three flags.

// allChannelsPayload → the PATCH body for the single switch: all three flags
// set to the same strict boolean (the endpoint accepts any subset).
export function allChannelsPayload(value) {
    const v = value === true;
    const out = {};
    VISIBILITY_FIELDS.forEach(f => {
        out[f] = v;
    });
    return out;
}

// combinedVisible → the single switch's value: shown on every channel.
// Missing flags read as shown (undefined ⇒ true, same as flagOf).
export function combinedVisible(row) {
    return VISIBILITY_FIELDS.every(f => flagOf(row, f));
}

// isMixed → the flags disagree (some channels on, some off — set via the API
// or before the single switch). The switch reads OFF for such a row.
export function isMixed(row) {
    const vals = VISIBILITY_FIELDS.map(f => flagOf(row, f));
    return vals.some(v => v) && vals.some(v => !v);
}

// isHiddenEverywhere → the row is switched off on all three channels (spec E2:
// muted row + "ซ่อนทุกช่องทาง" tag). A row missing any flag is NOT hidden.
export function isHiddenEverywhere(row) {
    if (!row) return false;
    return VISIBILITY_FIELDS.every(f => row[f] === false);
}

// visibilityReadyFrom → the list response's `visibility_ready`. Only an
// explicit `true` enables the switches; a missing key (older API) or `false`
// (SQL not applied yet — PATCH would answer 503) keeps them disabled (spec E1).
export function visibilityReadyFrom(body) {
    return !!(body && body.visibility_ready === true);
}

// visibilityFromResponse → the flags from a PATCH 200 body
// ({data:{id, show_web, show_member, show_app, updated_at}}), restricted to the
// keys the server actually returned as booleans. The response is the source of
// truth after a write (spec §6).
export function visibilityFromResponse(res) {
    const body = (res && res.data && res.data.data) || (res && res.data) || {};
    const out = {};
    VISIBILITY_FIELDS.forEach(f => {
        if (typeof body[f] === "boolean") out[f] = body[f];
    });
    return out;
}
